// ** React Imports
import { useEffect, useState } from 'react'

// ** Next Imports
import Head from 'next/head'
import { Router } from 'next/router'

/*
  Se quito el <Provider> de Redux.

  src/store/index.js combina seis slices —calendar, chat, user, permissions,
  email, invoice— que son todas de las apps demo del template, y cada una importa
  axios. Ningun archivo vivo del sitio usa useSelector ni useDispatch: las unicas
  pantallas que leian el store viven en src/_template-demos/ y src/views/apps/, y
  ninguna tiene ruta publicada.

  Atribuido con el source map del chunk _app: axios 35,3 KB, @reduxjs/toolkit
  9 KB, immer 8,8 KB mas los slices. Todo eso se bajaba y se parseaba en la
  primera carga de las 45 paginas para no usarse nunca.

  Los archivos de src/store/ quedan en el repo, igual que src/_template-demos/:
  lo que se corto es el import, no el codigo.
*/

// ** Loader Import
import NProgress from 'nprogress'

// ** Emotion Imports
import { CacheProvider } from '@emotion/react'

// ** Config Imports
import { defaultACLObj } from 'src/configs/acl'
// i18n removido: ver src/layouts/components/Translations.js
import themeConfig from 'src/configs/themeConfig'
// Firebase Analytics eliminado: Clarity cubre la analitica y el SDK pesaba en el chunk compartido.
// (Decia "Clarity + GA": no hay GA instalado, comprobado en las peticiones de produccion.)

// ** Fake-DB Import
// @fake-db eliminado: solo servia a las paginas demo del template (arrastraba jsonwebtoken + crypto-browserify)

// ** Third Party Import
import { Toaster } from 'react-hot-toast'

// ** Component Imports
import ThemeComponent from 'src/@core/theme/ThemeComponent'
import UserLayout from 'src/layouts/UserLayout'

// ** Spinner Import

// ** Contexts
import { SettingsConsumer, SettingsProvider } from 'src/@core/context/settingsContext'
import { AuthProvider } from 'src/context/AuthContext'

// ** Styled Components
import ReactHotToast from 'src/@core/styles/libs/react-hot-toast'

// ** Utils Imports
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'

// ** Prismjs Styles

// ** React Perfect Scrollbar Style
import 'react-perfect-scrollbar/dist/css/styles.css'
import 'src/iconify-bundle/icons-bundle-react'

// ** Global css styles
import '../../styles/globals.scss'

// ** MUI Imports
import CircularProgress from '@mui/material/CircularProgress'
import FallbackSpinner from 'src/@core/components/spinner'
import SeoHead from 'src/@core/components/seo/SeoHead'
import { iniciarAnalitica } from 'src/@core/utils/analitica'

const clientSideEmotionCache = createEmotionCache()

const clarityProjectId = 'w7gu40frqc'

/*
  Clarity ya no arranca en el scope del modulo.

  Estando ahi corria durante la evaluacion del chunk compartido, o sea antes de
  la hidratacion y en plena carrera por el LCP: Lighthouse en movil le medio
  127 ms de ejecucion, 25,3 KB de descarga y el bloqueo de main thread mas alto
  de todos los terceros. Ademas dispara la sincronizacion de cookies con
  bing.com, que suma cuatro peticiones mas.

  Analitica de sesion no necesita nada de eso antes de que la pagina este
  pintada. Ahora el paquete entra por import dinamico —asi tampoco viaja en el
  chunk critico— y se inicializa en el primer hueco de inactividad despues del
  load, con un setTimeout de respaldo para Safari, que no trae
  requestIdleCallback.
*/
const iniciarClarity = () => {
  let cancelado = false

  const arrancar = () => {
    if (cancelado) return
    import('@microsoft/clarity')
      .then(mod => (mod.default ?? mod).init(clarityProjectId))
      .catch(() => {})
  }

  const enHueco = () =>
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(arrancar, { timeout: 4000 })
      : window.setTimeout(arrancar, 2000)

  if (document.readyState === 'complete') {
    enHueco()
  } else {
    window.addEventListener('load', enHueco, { once: true })
  }

  return () => {
    cancelado = true
  }
}

// ** Pace Loader
if (themeConfig.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start()
  })
  Router.events.on('routeChangeError', () => {
    NProgress.done()
  })
  Router.events.on('routeChangeComplete', () => {
    NProgress.done()
  })
}

const Guard = ({ children, authGuard, guestGuard }) => {
  // if (guestGuard) {
  //   return <GuestGuard fallback={<Spinner />}>{children}</GuestGuard>
  // } else if (!guestGuard && !authGuard) {
  //   return <>{children}</>
  // } else {
  //   return <AuthGuard fallback={<Spinner />}>{children}</AuthGuard>
  // }
  return <>{children}</>
}

// ** Configure JSS & ClassName
const App = props => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props

  // Hooks
  const [loading, setLoading] = useState(true)

  // Variables
  const contentHeightFixed = Component.contentHeightFixed ?? false

  const getLayout =
    Component.getLayout ?? (page => <UserLayout contentHeightFixed={contentHeightFixed}>{page}</UserLayout>)
  const setConfig = Component.setConfig ?? undefined
  const authGuard = Component.authGuard ?? true
  const guestGuard = Component.guestGuard ?? false
  const aclAbilities = Component.acl ?? defaultACLObj

  useEffect(() => {
    const handleLoading = () => {
      setLoading(false)
    }

    const waitingForLoading = setTimeout(() => {
      handleLoading()
    }, 0)

    return () => clearTimeout(waitingForLoading)
  }, [])

  useEffect(iniciarClarity, [])
  useEffect(iniciarAnalitica, [])


  return (
    <CacheProvider value={emotionCache}>
        <Head>
          <meta name='viewport' content='initial-scale=1, width=device-width' />
          <meta name='google-adsense-account' content='ca-pub-8910558649748673' />
        </Head>

        {/*
          title, description, canonical, og:*, twitter:* y JSON-LD por ruta.
          Va despues del <Head> de arriba para que sus etiquetas ganen el duplicado,
          y antes de la pagina para que la pagina pueda seguir sobreescribiendo su
          propio title/description.

          Se quito <meta name='keywords'>: Google lo ignora desde 2009 y repetirlo
          identico en 45 paginas no aporta nada.
        */}
        <SeoHead />

        <AuthProvider>
          <SettingsProvider {...(setConfig ? { pageSettings: setConfig() } : {})}>
            <SettingsConsumer>
              {({ settings }) => {
                return (
                  <ThemeComponent settings={settings}>
                    <Guard authGuard={authGuard} guestGuard={guestGuard}>
                      {/* <AclGuard aclAbilities={aclAbilities} guestGuard={guestGuard} authGuard={authGuard}> */}
                      {(loading && !Component.noLoadingGate) ? <FallbackSpinner /> : getLayout(<Component {...pageProps} />)}
                      {/* </AclGuard> */}
                    </Guard>

                    {/* {getLayout(<Component {...pageProps} />)} */}
                    <ReactHotToast>
                      <Toaster position={settings.toastPosition} toastOptions={{ className: 'react-hot-toast' }} />
                    </ReactHotToast>
                  </ThemeComponent>
                )
              }}
            </SettingsConsumer>
          </SettingsProvider>
        </AuthProvider>
      </CacheProvider>
  )
}

export default App
